import Decimal from "decimal.js";
import {
    EarnExponentFinancials,
    EarnExponentWithdrawalPeriod,
    EarnVaultPositionResponseView,
    getOutstandingWithdrawalShares,
    isFulfilledUnclaimedWithdrawal,
    isOpenWithdrawalStatus,
    type EarnVaultReward,
    type EarnVaultRecordView,
    type EarnVaultSnapshotView,
    type GetTokenMetadata,
    type GetUsdPrice,
    type TokenListMetadata
} from "@bridgesplit/abf-react";
import {
    bigIntLamportsToUiAmount,
    bsMath,
    filterUndefined,
    formatDate,
    formatSeconds,
    TIME,
    toTokenAmount,
    tokenAmountToUiNumber,
    TokenAmount
} from "@bridgesplit/utils";
import type { EventTagKind } from "app/components/common";

const NAV_PER_SHARE_SCALE = 10n ** 18n;

export enum EarnEventKind {
    Deposit = 0,
    WithdrawInstant = 1,
    WithdrawalRequest = 2,
    WithdrawalCancel = 3,
    WithdrawalFulfill = 4,
    WithdrawalClaim = 5
}

const EARN_EVENT_TAG_KIND_MAP: Partial<Record<number, EventTagKind>> = {
    [EarnEventKind.Deposit]: "deposit",
    [EarnEventKind.WithdrawInstant]: "withdrawInstant",
    [EarnEventKind.WithdrawalRequest]: "withdrawRequest",
    [EarnEventKind.WithdrawalFulfill]: "withdrawFulfill",
    [EarnEventKind.WithdrawalClaim]: "withdrawProcessed",
    [EarnEventKind.WithdrawalCancel]: "withdrawCancel"
};

const EARN_EVENT_LABEL_MAP: Partial<Record<number, string>> = {
    [EarnEventKind.Deposit]: "Deposit",
    [EarnEventKind.WithdrawInstant]: "Withdraw",
    [EarnEventKind.WithdrawalRequest]: "Withdraw request",
    [EarnEventKind.WithdrawalFulfill]: "Withdraw fulfill",
    [EarnEventKind.WithdrawalClaim]: "Withdraw claim",
    [EarnEventKind.WithdrawalCancel]: "Withdraw cancel"
};

export function getEarnEventTagKind(eventKind: number | null | undefined, feesAssets?: bigint): EventTagKind {
    return (
        EARN_EVENT_TAG_KIND_MAP[eventKind ?? -1] ?? (feesAssets !== undefined && feesAssets > 0n ? "fee" : "activity")
    );
}

export function getEarnEventLabel(eventKind: number | null | undefined): string | undefined {
    return eventKind == null ? undefined : EARN_EVENT_LABEL_MAP[eventKind];
}

export function getEarnEventLabelOrActivity(eventKind: number | null | undefined): string {
    return getEarnEventLabel(eventKind) ?? "Activity";
}

function pow10(decimals: number): bigint {
    return 10n ** BigInt(decimals);
}

export function estimateSharesFromDeposit({
    amount,
    decimals,
    assetDecimals,
    shareDecimals,
    navPerShareE18
}: {
    amount: number | undefined;
    decimals: number | undefined;
    assetDecimals?: number | undefined;
    shareDecimals?: number | undefined;
    navPerShareE18: string | null | undefined;
}): bigint | undefined {
    const resolvedAssetDecimals = assetDecimals ?? decimals;
    const resolvedShareDecimals = shareDecimals ?? decimals;
    if (!amount || resolvedAssetDecimals === undefined || resolvedShareDecimals === undefined || !navPerShareE18) {
        return undefined;
    }

    const rawAmount = BigInt(new Decimal(amount).mul(new Decimal(10).pow(resolvedAssetDecimals)).floor().toFixed(0));
    return rawAssetsToRawShares({
        assets: rawAmount,
        navPerShareE18,
        shareDecimals: resolvedShareDecimals,
        assetDecimals: resolvedAssetDecimals
    });
}

// Display decimals for NAV / cost-per-share values. NAV-per-share hovers near 1.0 and moves in the
// 4th–6th decimal, so 2-decimal formatting collapses NAV and cost; use this everywhere they render.
export const NAV_PRECISION = 6;

// navPerShare is stored as a uint128 in e18 fixed-point on chain; convert through BigInt to
// stay out of Number-precision territory for typical share prices.
export function navPerShareE18ToNumber(navPerShareE18: string | null | undefined): number | undefined {
    if (!navPerShareE18) return undefined;
    try {
        return Number(BigInt(navPerShareE18)) / 1e18;
    } catch {
        return undefined;
    }
}

export function formatNavPerShareE18(
    navPerShareE18: string | null | undefined,
    displayDecimals = 6
): string | undefined {
    if (!navPerShareE18) return undefined;

    const navPerShare = BigInt(navPerShareE18);
    const whole = navPerShare / NAV_PER_SHARE_SCALE;
    const fraction = navPerShare % NAV_PER_SHARE_SCALE;
    const fractionDisplay = fraction.toString().padStart(18, "0").slice(0, displayDecimals).replace(/0+$/, "");

    return fractionDisplay ? `${whole.toString()}.${fractionDisplay}` : whole.toString();
}

export function getEarnVaultAumAssets(vault: EarnVaultRecordView | undefined): bigint | undefined {
    const financials = vault?.parsedAccountJson?.exponent?.financials;
    if (financials) {
        const aum = financials.aumInBase + financials.aumInBaseInPositions;
        if (aum > 0n) return aum;
    }
    return vault?.latestSnapshot?.totalAumAssets?.amount;
}

export function getEarnVaultAumLimitAssets(vault: EarnVaultRecordView | undefined): bigint | undefined {
    return vault?.parsedAccountJson?.exponent?.maxAumSupply;
}

export function summarizeEarnVaultSuppliedDeposits(vault: EarnVaultRecordView | undefined) {
    const principalLimit = getEarnVaultAumLimitAssets(vault);
    const principalSupplied = getEarnVaultAumAssets(vault) ?? 0n;
    const isDepositRestricted = principalLimit != null && principalLimit > 0n;
    const supplyLimitReached = isDepositRestricted ? principalSupplied >= principalLimit : false;
    const principalRemaining = isDepositRestricted
        ? principalLimit > principalSupplied
            ? principalLimit - principalSupplied
            : 0n
        : undefined;

    return {
        isDepositRestricted,
        principalSupplied,
        principalLimit,
        principalRemaining,
        supplyLimitReached
    };
}

export function isEarnVaultSupplyAvailable(vault: EarnVaultRecordView | undefined) {
    return !summarizeEarnVaultSuppliedDeposits(vault).supplyLimitReached;
}

export interface EarnVaultApyBreakdown {
    displayApy: number | undefined;
    baseApy: number | undefined;
    rewardApy: number | undefined;
    headlineApy: number | undefined;
}

/** Base falls back to realized 7d when the vault has no display APY; rewards stack on either. */
export function getEarnVaultApyBreakdown(snapshot: EarnVaultSnapshotView | null | undefined): EarnVaultApyBreakdown {
    const displayApy = snapshot?.displayApyBps ?? undefined;
    const rewardApy = snapshot?.rewardsApyBps ?? undefined;
    const baseApy = displayApy ?? snapshot?.realizedReturnBps7d ?? undefined;
    const computedHeadline = baseApy == null && rewardApy == null ? undefined : (baseApy ?? 0) + (rewardApy ?? 0);
    const headlineApy = snapshot?.headlineApyBps ?? computedHeadline;
    return { displayApy, baseApy, rewardApy, headlineApy };
}

export interface EarnVaultStatsView {
    displayApy: number | undefined;
    headlineApy: number | undefined;
    baseApy: number | undefined;
    rewardApy: number | undefined;
    realizedReturn24h: number | undefined;
    realizedReturn7d: number | undefined;
    realizedReturn30d: number | undefined;
    utilization: number | undefined;
    aum: { amount: bigint | undefined; decimals: number | undefined; symbol: string | undefined };
    aumUi: number | undefined;
    aumUsd: number | undefined;
}

export function buildEarnVaultStatsView(
    vault: EarnVaultRecordView | undefined,
    {
        getMetadata,
        getUsdPrice
    }: {
        getMetadata: GetTokenMetadata;
        getUsdPrice?: GetUsdPrice;
    }
): EarnVaultStatsView {
    const metadata = getMetadata(vault?.acceptedAssetMint);
    const amount = getEarnVaultAumAssets(vault);
    const aumUi = rawToUiNumber(amount, metadata?.decimals);
    const price = getUsdPrice?.(vault?.acceptedAssetMint);
    const aumUsd = aumUi != null && Number.isFinite(aumUi) && price != null ? bsMath.mul(aumUi, price) : undefined;
    const snapshot = vault?.latestSnapshot;
    const { displayApy, baseApy, rewardApy, headlineApy } = getEarnVaultApyBreakdown(snapshot);
    const realizedReturn24h = snapshot?.realizedReturnBps24h ?? undefined;
    const realizedReturn7d = snapshot?.realizedReturnBps7d ?? undefined;
    const realizedReturn30d = snapshot?.realizedReturnBps30d ?? undefined;

    return {
        displayApy,
        headlineApy,
        baseApy,
        rewardApy,
        realizedReturn24h,
        realizedReturn7d,
        realizedReturn30d,
        utilization: snapshot?.utilizationBps ?? undefined,
        aum: { amount, decimals: metadata?.decimals, symbol: metadata?.symbol },
        aumUi,
        aumUsd
    };
}

export function buildEarnVaultIconMetadata(
    vault: EarnVaultRecordView | undefined,
    getMetadata: GetTokenMetadata
): TokenListMetadata[] | undefined {
    if (!vault) return undefined;
    const displayMints = vault.metadataJson?.displayAssetMints;
    const mints = displayMints?.length ? displayMints : [vault.acceptedAssetMint];
    const list = mints.map((mint) => getMetadata(mint)).filter(filterUndefined);
    return list.length ? list : undefined;
}

export function rawAssetsFromUiAmount(amount: number | undefined, decimals: number | undefined): bigint | undefined {
    if (amount == null || decimals == null) return undefined;
    return BigInt(new Decimal(amount).mul(new Decimal(10).pow(decimals)).floor().toFixed(0));
}

/** Supply caps are raw underlying assets; inputs and Max use the selected deposit token. */
export function getEarnDepositSupplyLimit({
    amount,
    principalRemaining,
    depositDecimals,
    acceptedDecimals,
    priceRatio
}: {
    amount: number | undefined;
    principalRemaining: bigint | undefined;
    depositDecimals: number | undefined;
    acceptedDecimals: number | undefined;
    priceRatio: number | undefined;
}) {
    const canConvert =
        depositDecimals != null &&
        acceptedDecimals != null &&
        priceRatio != null &&
        Number.isFinite(priceRatio) &&
        priceRatio > 0;
    if (principalRemaining == null || !canConvert) {
        return {
            remainingDepositAmount: undefined,
            exceedsSupplyLimit: false,
            isSupplyLimitLoading: principalRemaining != null
        };
    }

    const acceptedScale = new Decimal(10).pow(acceptedDecimals);
    const remainingDepositAmount = new Decimal(principalRemaining.toString())
        .div(acceptedScale)
        .div(priceRatio)
        .toDecimalPlaces(depositDecimals, Decimal.ROUND_DOWN)
        .toNumber();
    const rawDeposit = rawAssetsFromUiAmount(amount, depositDecimals);
    const underlyingAmount =
        rawDeposit == null
            ? undefined
            : new Decimal(rawDeposit.toString())
                  .div(new Decimal(10).pow(depositDecimals))
                  .mul(priceRatio)
                  .mul(acceptedScale);

    return {
        remainingDepositAmount,
        exceedsSupplyLimit: underlyingAmount?.gt(principalRemaining.toString()) ?? false,
        isSupplyLimitLoading: false
    };
}

export function capDepositMaxToRemaining(
    maxAmount: number | undefined,
    remainingDepositAmount: number | undefined
): number | undefined {
    if (maxAmount == null) return maxAmount;
    if (remainingDepositAmount == null) return maxAmount;
    return Math.min(maxAmount, remainingDepositAmount);
}

export function getEarnVaultNavPerShareE18({
    vault
}: {
    vault: EarnVaultRecordView | undefined;
    shareDecimals: number | undefined;
    assetDecimals: number | undefined;
}): string | null | undefined {
    return vault?.latestSnapshot?.navPerShareE18;
}

export function rawSharesToRawAssets({
    shares,
    navPerShareE18,
    shareDecimals,
    assetDecimals
}: {
    shares: bigint | undefined;
    navPerShareE18: string | null | undefined;
    shareDecimals: number | undefined;
    assetDecimals: number | undefined;
}): bigint | undefined {
    if (shares == null || !navPerShareE18 || shareDecimals == null || assetDecimals == null) return undefined;

    return (shares * BigInt(navPerShareE18) * pow10(assetDecimals)) / (NAV_PER_SHARE_SCALE * pow10(shareDecimals));
}

export function rawAssetsToRawShares({
    assets,
    navPerShareE18,
    shareDecimals,
    assetDecimals
}: {
    assets: bigint | undefined;
    navPerShareE18: string | null | undefined;
    shareDecimals: number | undefined;
    assetDecimals: number | undefined;
}): bigint | undefined {
    if (assets == null || !navPerShareE18 || shareDecimals == null || assetDecimals == null) return undefined;

    const navPerShare = BigInt(navPerShareE18);
    if (navPerShare === 0n) return undefined;

    return (assets * NAV_PER_SHARE_SCALE * pow10(shareDecimals)) / (navPerShare * pow10(assetDecimals));
}

const BPS_SCALE = 10_000n;

/**
 * Instant-withdraw headroom in raw asset units, mirroring the vault program's two checks:
 * - reserve: `totalAum * reservesShareBp` minus the value of queued (unfilled) withdrawals
 * - window: `cap - used` while the window is open; once expired (or never opened, end = 0) the next
 *   instant withdraw opens a fresh window with cap `totalAum * (windowLimitBp || reservesShareBp)`
 * Share-bp inputs are the converted UI decimals (0.025 = 250 bps). Undefined means unknown; don't block.
 */
export function getInstantWithdrawAvailableAssets({
    financials,
    reservesShare,
    windowLimitShare,
    pendingWithdrawalAssets,
    nowSeconds
}: {
    financials: EarnExponentFinancials | undefined;
    reservesShare: number | undefined;
    windowLimitShare: number | undefined;
    pendingWithdrawalAssets: bigint | undefined;
    nowSeconds: number;
}): bigint | undefined {
    if (!financials || !reservesShare) return undefined;
    const totalAum = financials.aumInBase + financials.aumInBaseInPositions;
    const reservesBp = BigInt(Math.round(reservesShare * Number(BPS_SCALE)));
    const windowBp = windowLimitShare ? BigInt(Math.round(windowLimitShare * Number(BPS_SCALE))) : reservesBp;

    const reserve = (totalAum * reservesBp) / BPS_SCALE - (pendingWithdrawalAssets ?? 0n);
    const windowOpen = financials.instantWithdrawalWindowEnd > BigInt(nowSeconds);
    const window = windowOpen
        ? financials.instantWithdrawalWindowCapAum - financials.instantWithdrawnAumInWindow
        : (totalAum * windowBp) / BPS_SCALE;

    const available = reserve < window ? reserve : window;
    return available > 0n ? available : 0n;
}

/** A token entry's idle reserve expressed in the vault's accepted asset base units. */
export function getPayoutReserveInAcceptedUnits({
    reserve,
    reserveDecimals,
    acceptedDecimals,
    priceRatio
}: {
    reserve: bigint | undefined;
    reserveDecimals: number | undefined;
    acceptedDecimals: number | undefined;
    priceRatio: number | undefined;
}): bigint | undefined {
    if (reserve == null || reserveDecimals == null || acceptedDecimals == null || !priceRatio) return undefined;
    return BigInt(
        new Decimal(reserve.toString())
            .mul(priceRatio)
            .mul(new Decimal(10).pow(acceptedDecimals - reserveDecimals))
            .floor()
            .toFixed(0)
    );
}

export function rawToUiNumber(rawAmount: bigint | undefined, decimals: number | undefined): number | undefined {
    if (rawAmount == null || decimals == null) return undefined;
    if (rawAmount < 0n) return -Number(bigIntLamportsToUiAmount(-rawAmount, decimals));
    return Number(bigIntLamportsToUiAmount(rawAmount, decimals));
}

export interface EarnPortfolioPositionView {
    response: EarnVaultPositionResponseView;
    shares: TokenAmount | undefined;
    assets: TokenAmount | undefined;
    valueUsd: number | undefined;
    costBasis: TokenAmount | undefined;
    costBasisUsd: number | undefined;
    avgCostBasisPerShare: number | undefined;
    unrealizedPnl: TokenAmount | undefined;
    unrealizedPnlUsd: number | undefined;
    unrealizedPnlPercent: number | undefined;
    realizedPnl: TokenAmount | undefined;
    realizedPnlUsd: number | undefined;
    feesPaid: TokenAmount | undefined;
    feesPaidUsd: number | undefined;
    displayApy: number | undefined;
    baseApy: number | undefined;
    rewardApy: number | undefined;
    headlineApy: number | undefined;
    realizedReturn7d: number | undefined;
    realizedReturn30d: number | undefined;
    dailyPoints: number | undefined;
    pointsReward: EarnVaultReward | undefined;
    pendingRequestCount: number;
    fulfilledUnclaimedCount: number;
    fulfilledUnclaimed: TokenAmount | undefined;
    fulfilledUnclaimedUsd: number | undefined;
}

export function buildEarnPositionView(
    response: EarnVaultPositionResponseView,
    {
        getMetadata,
        getUsdPrice
    }: {
        getMetadata: GetTokenMetadata;
        getUsdPrice: GetUsdPrice;
    }
): EarnPortfolioPositionView {
    const { vault, position } = response;
    const acceptedDecimals = getMetadata(vault.acceptedAssetMint)?.decimals;
    const shareDecimals = getMetadata(vault.shareMint)?.decimals ?? acceptedDecimals;
    const shareBalance = position?.shareBalance?.amount;
    const navPerShareE18 = getEarnVaultNavPerShareE18({
        vault,
        shareDecimals,
        assetDecimals: acceptedDecimals
    });
    const price = getUsdPrice(vault.acceptedAssetMint);
    const snapshot = vault.latestSnapshot;
    const { displayApy, baseApy, rewardApy, headlineApy } = getEarnVaultApyBreakdown(snapshot);
    const realizedReturn7d = snapshot?.realizedReturnBps7d ?? undefined;
    const realizedReturn30d = snapshot?.realizedReturnBps30d ?? undefined;

    const toAssetAmount = (amount: bigint | undefined): TokenAmount | undefined =>
        amount == null || acceptedDecimals == null ? undefined : toTokenAmount(amount, acceptedDecimals);
    const toUsd = (amount: TokenAmount | undefined) => {
        const ui = tokenAmountToUiNumber(amount);
        return ui != null && price != null ? ui * price : undefined;
    };
    const toAssets = (shares: bigint | undefined) =>
        rawSharesToRawAssets({ shares, navPerShareE18, shareDecimals, assetDecimals: acceptedDecimals });

    const fulfilledUnclaimedRequests = response.openWithdrawalRequests.filter(isFulfilledUnclaimedWithdrawal);
    const fulfilledUnclaimedShares = fulfilledUnclaimedRequests.reduce(
        (sum, request) => sum + (request.sharesFilled.amount - request.sharesClaimed.amount),
        0n
    );

    const shares =
        shareBalance == null || shareDecimals == null ? undefined : toTokenAmount(shareBalance, shareDecimals);
    const assets = toAssetAmount(toAssets(shareBalance));
    const costBasis = position?.costBasisAssets;
    const realizedPnl = position?.realizedPnlAssets;
    const feesPaid = position?.feesPaidAssets;
    const fulfilledUnclaimed = toAssetAmount(toAssets(fulfilledUnclaimedShares));

    // Queued shares leave the wallet but cost basis still covers them, so PnL must value them too.
    const heldShareBalance =
        shareBalance == null
            ? undefined
            : shareBalance + getOutstandingWithdrawalShares(response.openWithdrawalRequests);
    const heldAssets = toAssetAmount(toAssets(heldShareBalance));

    const valueUsd = toUsd(assets);
    const heldValueUsd = toUsd(heldAssets);
    const costBasisUsd = toUsd(costBasis);
    const unrealizedPnl =
        heldAssets && costBasis ? toTokenAmount(heldAssets.amount - costBasis.amount, heldAssets.decimals) : undefined;
    const unrealizedPnlUsd = heldValueUsd != null && costBasisUsd != null ? heldValueUsd - costBasisUsd : undefined;

    const heldSharesUiNumber =
        heldShareBalance == null || shareDecimals == null
            ? undefined
            : tokenAmountToUiNumber(toTokenAmount(heldShareBalance, shareDecimals));
    const costBasisUiNumber = tokenAmountToUiNumber(costBasis);

    return {
        response,
        shares,
        assets,
        valueUsd,
        costBasis,
        costBasisUsd,
        avgCostBasisPerShare:
            costBasisUiNumber != null && heldSharesUiNumber && heldSharesUiNumber > 0
                ? costBasisUiNumber / heldSharesUiNumber
                : undefined,
        unrealizedPnl,
        unrealizedPnlUsd,
        unrealizedPnlPercent:
            unrealizedPnlUsd != null && costBasisUsd != null && costBasisUsd > 0
                ? unrealizedPnlUsd / costBasisUsd
                : undefined,
        realizedPnl,
        realizedPnlUsd: toUsd(realizedPnl),
        feesPaid,
        feesPaidUsd: toUsd(feesPaid),
        displayApy,
        baseApy,
        rewardApy,
        headlineApy,
        realizedReturn7d,
        realizedReturn30d,
        dailyPoints: undefined,
        pointsReward: undefined,
        pendingRequestCount: response.openWithdrawalRequests.filter((request) => isOpenWithdrawalStatus(request.status))
            .length,
        fulfilledUnclaimedCount: fulfilledUnclaimedRequests.length,
        fulfilledUnclaimed,
        fulfilledUnclaimedUsd: toUsd(fulfilledUnclaimed)
    };
}

// ── Withdrawal window (Exponent) ────────────────────────────────────────────────
//
// Exponent gates withdrawals but ships no unlock/day-of-week helper (only the `WithdrawalPeriodSettings`
// codec), so this encodes the vault owner's spec, confirmed against the live "Onre Growth" vault:
//   - order: request queued → unlocks → manager fills → user claims
//   - unlock = 00:00 UTC after an eligible withdrawal day ends
//   - a request queued within the last 4h before an unlock rolls to the following eligible window
//   - `daysOfWeek` is a Sunday-indexed bitmask (bit 0 = Sunday; e.g. 36 = bits 2,5 = Tue, Fri)

const MS_PER_SECOND = 1000;
const DAY_MS = TIME.DAY * MS_PER_SECOND;
const WEEK_MS = TIME.WEEK * MS_PER_SECOND;
const DAYS_PER_WEEK = 7;
/** A request queued within this window before an unlock rolls to the next eligible window. */
const WITHDRAWAL_UNLOCK_CUTOFF_MS = 4 * TIME.HOUR * MS_PER_SECOND;

/**
 * Every eligible unlock instant (ms) — 00:00 UTC after an eligible withdrawal day — from `fromMs`
 * forward. Scans a full cadence plus a buffer day so the last-4h roll-forward always lands in range.
 */
function scanEligibleUnlocksMs(
    fromMs: number,
    withdrawalPeriod: Exclude<EarnExponentWithdrawalPeriod, number>
): number[] {
    const { daysOfWeek, referenceTimestamp } = withdrawalPeriod;
    if (!daysOfWeek) return [];

    const weekInterval = Math.max(withdrawalPeriod.weekInterval || 1, 1);
    const fromDayStartMs = startOfUtcDayMs(fromMs);
    const daysToScan = (weekInterval + 1) * DAYS_PER_WEEK + 1;
    const unlocks: number[] = [];

    for (let offset = 0; offset <= daysToScan; offset++) {
        const dayStartMs = fromDayStartMs + offset * DAY_MS;
        if (!isEligibleWithdrawalWeekday(dayStartMs, daysOfWeek)) continue;
        if (!isActiveWithdrawalWeek(dayStartMs, referenceTimestamp, weekInterval)) continue;
        unlocks.push(dayStartMs + DAY_MS);
    }
    return unlocks;
}

/** Next unlock instant (ms) for each distinct eligible weekday from `fromMs`, for schedule display. */
function upcomingWeeklyUnlocksMs(
    withdrawalPeriod: Exclude<EarnExponentWithdrawalPeriod, number>,
    fromMs: number
): number[] {
    const seenUtcWeekdays = new Set<number>();
    return scanEligibleUnlocksMs(fromMs, withdrawalPeriod).filter((unlockMs) => {
        const utcWeekday = new Date(unlockMs - DAY_MS).getUTCDay();
        if (seenUtcWeekdays.has(utcWeekday)) return false;
        seenUtcWeekdays.add(utcWeekday);
        return true;
    });
}

/** Human-readable withdrawal schedule, localized to the viewer's timezone + locale (matching `formatDate`). */
export function formatWithdrawalSchedule(
    withdrawalPeriod: EarnExponentWithdrawalPeriod | undefined,
    nowMs: number
): string {
    if (withdrawalPeriod === undefined) return "";
    // Interval mode: a simple lock-time cooldown from queue time.
    if (typeof withdrawalPeriod === "number") return formatSeconds(withdrawalPeriod, { maxUnit: "day" });
    if (withdrawalPeriod.kind !== "WeeklySchedule") return withdrawalPeriod.kind;

    const unlocks = upcomingWeeklyUnlocksMs(withdrawalPeriod, nowMs);
    if (!unlocks.length) return "Weekly";

    const localDays = unlocks
        .map((ms) => ({ ms, localWeekday: new Date(ms).getDay() }))
        .sort((a, b) => a.localWeekday - b.localWeekday)
        .map(({ ms }) => new Date(ms).toLocaleString([], { weekday: "short" }));
    const uniqueDays = Array.from(new Set(localDays));
    const timeLabel = formatDate(Math.min(...unlocks), "time");
    const cadence = withdrawalPeriod.weekInterval > 1 ? `Every ${withdrawalPeriod.weekInterval} weeks · ` : "";
    return `${cadence}${uniqueDays.join(", ")} · ${timeLabel}`;
}

/** Instant (ms) a withdrawal queued at `requestedAtMs` becomes claimable, or undefined when no `withdrawalPeriod` is set. */
export function computeWithdrawalUnlockMs(
    withdrawalPeriod: EarnExponentWithdrawalPeriod | undefined,
    requestedAtMs: number
): number | undefined {
    if (withdrawalPeriod === undefined || !Number.isFinite(requestedAtMs)) return undefined;
    // Interval mode: exact lock-time cooldown from queue time.
    if (typeof withdrawalPeriod === "number") return requestedAtMs + withdrawalPeriod * MS_PER_SECOND;
    if (withdrawalPeriod.kind !== "WeeklySchedule") return undefined;
    return computeWeeklyWithdrawalUnlockMs(requestedAtMs, withdrawalPeriod);
}

export type WithdrawalClaimState = {
    /** Instant (ms) the request unlocks, or undefined when the vault has no `withdrawalPeriod`. */
    unlockMs: number | undefined;
    canClaim: boolean;
    /** Filled but not yet unlocked → show a guaranteed "Unlocks in …" countdown. */
    isAwaitingWindow: boolean;
    /** Not yet filled by the manager → show "Processing" (no promised claim time). */
    isProcessing: boolean;
};

/**
 * Claim state for a queued withdrawal: claimable only once it's filled (`sharesFilled > sharesClaimed`)
 * and its unlock time has passed. Centralizes the fill/unlock gating shared by the queue view.
 */
export function deriveWithdrawalClaimState({
    withdrawalPeriod,
    requestedAtMs,
    nowMs,
    isOpen,
    sharesRequested,
    sharesFilled,
    sharesClaimed
}: {
    withdrawalPeriod: EarnExponentWithdrawalPeriod | undefined;
    requestedAtMs: number;
    nowMs: number;
    isOpen: boolean;
    sharesRequested: bigint;
    sharesFilled: bigint;
    sharesClaimed: bigint;
}): WithdrawalClaimState {
    const hasClaimable = sharesFilled > sharesClaimed;
    const hasPendingShares = sharesFilled < sharesRequested;
    // The unlock instant depends only on request time + schedule, never on open/closed status: a fully
    // filled request flips to `Fulfilled` (not "open"), and gating this on `isOpen` would drop the time
    // gate and make it claimable the instant the manager fills it, ignoring the withdrawal window.
    const unlockMs = computeWithdrawalUnlockMs(withdrawalPeriod, requestedAtMs);
    const isUnlocked = unlockMs === undefined || nowMs >= unlockMs;
    return {
        unlockMs,
        canClaim: hasClaimable && isUnlocked,
        isAwaitingWindow: hasClaimable && !isUnlocked,
        isProcessing: !hasClaimable && isOpen && hasPendingShares
    };
}

function computeWeeklyWithdrawalUnlockMs(
    requestedAtMs: number,
    withdrawalPeriod: Exclude<EarnExponentWithdrawalPeriod, number>
): number {
    if (!withdrawalPeriod.daysOfWeek) return requestedAtMs;

    // Unlock at the first eligible instant that the request doesn't fall inside the last-4h cutoff of.
    const unlockMs = scanEligibleUnlocksMs(requestedAtMs, withdrawalPeriod).find(
        (candidate) => requestedAtMs <= candidate - WITHDRAWAL_UNLOCK_CUTOFF_MS
    );
    if (unlockMs !== undefined) return unlockMs;

    // No eligible day in range: fall back to one cadence out.
    const weekInterval = Math.max(withdrawalPeriod.weekInterval || 1, 1);
    return startOfUtcDayMs(requestedAtMs) + weekInterval * WEEK_MS;
}

/** Compact "2d 4h" / "4h 3m" / "5m" countdown to `unlockMs`, for the pending-row caption. */
export function formatWithdrawalCountdown(unlockMs: number, nowMs: number): string {
    const totalMinutes = Math.floor(Math.max(unlockMs - nowMs, 0) / (TIME.MINUTE * MS_PER_SECOND));
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m`;
    return "<1m";
}

function startOfUtcDayMs(timestampMs: number): number {
    const date = new Date(timestampMs);
    return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

function startOfUtcWeekMs(timestampMs: number): number {
    const dayStartMs = startOfUtcDayMs(timestampMs);
    return dayStartMs - new Date(dayStartMs).getUTCDay() * DAY_MS;
}

function isEligibleWithdrawalWeekday(dayStartMs: number, daysOfWeek: number): boolean {
    return (daysOfWeek & (1 << new Date(dayStartMs).getUTCDay())) !== 0;
}

function isActiveWithdrawalWeek(
    dayStartMs: number,
    referenceTimestamp: number | undefined,
    weekInterval: number
): boolean {
    if (weekInterval <= 1) return true;
    const referenceWeekMs = startOfUtcWeekMs((referenceTimestamp || 0) * MS_PER_SECOND);
    const candidateWeekMs = startOfUtcWeekMs(dayStartMs);
    const weeksSinceReference = Math.floor((candidateWeekMs - referenceWeekMs) / WEEK_MS);
    return weeksSinceReference >= 0 && weeksSinceReference % weekInterval === 0;
}
